import { useState, useEffect, ReactElement, Fragment as ReactFragment } from "react";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  onSnapshot,
  updateDoc,
  Timestamp,
  FieldValue,
} from "firebase/firestore";
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Stack,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Workspace, WorkspaceMemberWithDetails, WorkspacePermissions, WorkspaceRole, WorkspaceSettings as WorkspaceSettingsType, CustomRole } from "../types";
import LoadingSpinner from "../components/LoadingSpinner";
import InfoTooltipIcon from "../components/InfoTooltipIcon";
import { isDemoMode } from "../utils/demoMode";
import { DemoModeMessage } from "../utils/demoModeMessage";
import CreateRoleDialog from "./PermissionsSettings/components/CreateRoleDialog";
import EditRoleDialog from "./PermissionsSettings/components/EditRoleDialog";
import DeleteRoleDialog from "./PermissionsSettings/components/DeleteRoleDialog";

// Cache for permissions page data so re-entering the page shows content immediately (like other settings)
const PERMISSIONS_CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
type PermissionsSettingsCacheEntry = {
  workspace: Workspace | null;
  members: WorkspaceMemberWithDetails[];
  permissions: WorkspacePermissions;
  customRoles: CustomRole[];
  customRoleNames: Record<string, string>;
  isOwner: boolean;
  currentUserRole: WorkspaceRole;
  timestamp: number;
};
const permissionsSettingsCache = new Map<string, PermissionsSettingsCacheEntry>();

const DEFAULT_PERMISSIONS: WorkspacePermissions = {
  manageWorkspaceConfig: ["owner", "admin"],
  manageMembers: ["owner", "admin"],
  archiveAccounts: ["owner"],
  deleteTransactions: ["owner"],
  editTransactions: ["owner", "admin"],
  viewTransactionHistory: ["owner", "admin"],
  viewPermissions: ["owner", "admin"],
  editPermissions: ["owner"],
  editPreferences: ["owner", "admin"],
  addCurrency: ["owner", "admin"],
  changeDefaultCurrency: ["owner", "admin"],
  changeCurrencyRateDecimalPlaces: ["owner", "admin"],
};

function updatePermissionsCache(
  workspaceId: string,
  partial: Partial<Omit<PermissionsSettingsCacheEntry, "timestamp">>
): void {
  const current = permissionsSettingsCache.get(workspaceId);
  permissionsSettingsCache.set(workspaceId, {
    workspace: current?.workspace ?? null,
    members: current?.members ?? [],
    permissions: current?.permissions ?? DEFAULT_PERMISSIONS,
    customRoles: current?.customRoles ?? [],
    customRoleNames: current?.customRoleNames ?? {},
    isOwner: current?.isOwner ?? false,
    currentUserRole: current?.currentUserRole ?? "user",
    ...partial,
    timestamp: Date.now(),
  });
}

/**
 * Preload permissions page data so the Permissions tab shows immediately when opened.
 * Call when the parent (e.g. Members & Permissions) mounts.
 */
export async function preloadPermissionsSettings(workspaceId: string): Promise<void> {
  const user = auth.currentUser;
  if (!user && !isDemoMode()) return;

  const cached = permissionsSettingsCache.get(workspaceId);
  if (cached && Date.now() - cached.timestamp < PERMISSIONS_CACHE_EXPIRY_MS) return;

  try {
    const workspaceSnap = await getDoc(doc(db, "workspaces", workspaceId));
    if (!workspaceSnap.exists()) return;
    const wData = workspaceSnap.data();
    const workspace: Workspace = {
      id: workspaceSnap.id,
      name: wData.name as string,
      ownerId: wData.ownerId as string,
      logoUrl: wData.logoUrl as string | undefined,
      createdAt: wData.createdAt,
    };

    const settingsSnap = await getDoc(doc(db, "workspaces", workspaceId, "settings", "general"));
    let permissions = DEFAULT_PERMISSIONS;
    let customRoles: CustomRole[] = [];
    let customRoleNames: Record<string, string> = {};
    if (settingsSnap.exists()) {
      const data = settingsSnap.data() as WorkspaceSettingsType;
      if (data.permissions) {
        permissions = {
          manageWorkspaceConfig: data.permissions.manageWorkspaceConfig || ["owner", "admin"],
          manageMembers: data.permissions.manageMembers || ["owner", "admin"],
          archiveAccounts: data.permissions.archiveAccounts || ["owner"],
          deleteTransactions: data.permissions.deleteTransactions || ["owner"],
          editTransactions: data.permissions.editTransactions || ["owner", "admin"],
          viewTransactionHistory: data.permissions.viewTransactionHistory || ["owner", "admin"],
          viewPermissions: data.permissions.viewPermissions || ["owner", "admin"],
          editPermissions: data.permissions.editPermissions || ["owner"],
          editPreferences: data.permissions.editPreferences || ["owner", "admin"],
          addCurrency: data.permissions.addCurrency || ["owner", "admin"],
          changeDefaultCurrency: data.permissions.changeDefaultCurrency || ["owner", "admin"],
          changeCurrencyRateDecimalPlaces: data.permissions.changeCurrencyRateDecimalPlaces || ["owner", "admin"],
        };
      }
      customRoles = data.customRoles ?? [];
      customRoleNames = data.customRoleNames ?? {};
    }

    const membersSnap = await getDocs(collection(db, "workspaces", workspaceId, "members"));
    const membersList: WorkspaceMemberWithDetails[] = await Promise.all(
      membersSnap.docs.map(async (memberDoc) => {
        const memberData = memberDoc.data();
        const userId = memberDoc.id;
        const role = workspace.ownerId === userId ? "owner" : (memberData.role as "owner" | "member" | "admin");
        let name: string | undefined = memberData.displayName as string | undefined;
        let email: string | undefined = memberData.email as string | undefined;
        let photoURL: string | undefined;
        try {
          const userDoc = await getDoc(doc(db, "users", userId));
          if (userDoc.exists()) {
            const u = userDoc.data();
            name = (u.name as string) || (u.displayName as string) || name;
            email = u.email as string || email;
            photoURL = u.photoURL as string | undefined;
          }
        } catch {
          // ignore
        }
        return {
          id: userId,
          role,
          joinedAt: memberData.joinedAt,
          name,
          email,
          photoURL,
          status: "active" as const,
        };
      })
    );

    const ownerInList = membersList.find((m) => m.id === workspace.ownerId);
    if (!ownerInList) {
      membersList.unshift({
        id: workspace.ownerId,
        role: "owner",
        joinedAt: workspace.createdAt,
        name: undefined,
        email: undefined,
        photoURL: undefined,
        status: "active",
      });
    } else {
      const i = membersList.findIndex((m) => m.id === workspace.ownerId);
      if (i > 0) {
        const [owner] = membersList.splice(i, 1);
        membersList.unshift(owner);
      }
    }
    membersList.sort((a, b) => {
      if (a.id === workspace.ownerId) return -1;
      if (b.id === workspace.ownerId) return 1;
      return (a.name || a.email || "").localeCompare(b.name || b.email || "");
    });

    let isOwner = false;
    let currentUserRole: WorkspaceRole = "user";
    if (user) {
      if (user.uid === workspace.ownerId) {
        isOwner = true;
        currentUserRole = "owner";
      } else {
        const currentMember = membersList.find((m) => m.id === user.uid);
        if (currentMember) {
          currentUserRole = (currentMember.role === "member" ? "user" : currentMember.role) as WorkspaceRole;
          isOwner = currentUserRole === "owner";
        }
      }
    }

    updatePermissionsCache(workspaceId, {
      workspace,
      members: membersList,
      permissions,
      customRoles,
      customRoleNames,
      isOwner,
      currentUserRole,
    });
  } catch (err) {
    console.warn("Permissions preload failed:", err);
  }
}

interface PermissionsSettingsProps {
  workspaceId: string;
  /** When true, use full page width (no horizontal padding). Used when embedded in Members & Permissions. */
  fullWidth?: boolean;
}

function PermissionsSettings({ workspaceId, fullWidth = false }: PermissionsSettingsProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const cached = permissionsSettingsCache.get(workspaceId);
  const hasValidCache = !!(cached && Date.now() - cached.timestamp < PERMISSIONS_CACHE_EXPIRY_MS);

  const [workspace, setWorkspace] = useState<Workspace | null>(() => (hasValidCache ? cached!.workspace : null));
  const [members, setMembers] = useState<WorkspaceMemberWithDetails[]>(() => (hasValidCache ? cached!.members : []));
  const [loading, setLoading] = useState<boolean>(!hasValidCache);
  const [isOwner, setIsOwner] = useState<boolean>(() => (hasValidCache ? cached!.isOwner : false));
  const [currentUserRole, setCurrentUserRole] = useState<WorkspaceRole>(() => (hasValidCache ? cached!.currentUserRole : "user"));

  // Permissions settings
  const [permissions, setPermissions] = useState<WorkspacePermissions>(() =>
    hasValidCache ? cached!.permissions : DEFAULT_PERMISSIONS
  );
  const [savingPermissionKey, setSavingPermissionKey] = useState<keyof WorkspacePermissions | null>(null);
  const [savingRole, setSavingRole] = useState<WorkspaceRole | null>(null);
  
  // Custom roles
  const [customRoles, setCustomRoles] = useState<CustomRole[]>(() => (hasValidCache ? cached!.customRoles : []));
  const [customRoleNames, setCustomRoleNames] = useState<Record<string, string>>(() =>
    hasValidCache ? cached!.customRoleNames : {}
  );
  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState<boolean>(false);
  const [creatingRole, setCreatingRole] = useState<boolean>(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<boolean>(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editingRoleName, setEditingRoleName] = useState<string>("");
  const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState<boolean>(false);
  const [deletingRole, setDeletingRole] = useState<boolean>(false);
  const [deletingRoleId, setDeletingRoleId] = useState<string | null>(null);
  const [deletingRoleName, setDeletingRoleName] = useState<string>("");
  
  // Snackbar for demo mode alerts
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarElement, setSnackbarElement] = useState<React.ReactElement | null>(null);

  useEffect(() => {
    // Allow loading in demo mode or when authenticated
    const user = auth.currentUser;
    if (!user && !isDemoMode()) return;

    // Fetch workspace data
    const fetchWorkspace = async (): Promise<Workspace | null> => {
      try {
        const workspaceDoc = await getDoc(doc(db, "workspaces", workspaceId));
        if (workspaceDoc.exists()) {
          const data = workspaceDoc.data();
          const workspaceData: Workspace = {
            id: workspaceDoc.id,
            name: data.name as string,
            ownerId: data.ownerId as string,
            logoUrl: data.logoUrl as string | undefined,
            createdAt: data.createdAt,
          };
          setWorkspace(workspaceData);
          updatePermissionsCache(workspaceId, { workspace: workspaceData });
          // Check if user is primary owner - set immediately if true
          if (user) {
            const isPrimaryOwner = user.uid === workspaceData.ownerId;
            if (isPrimaryOwner) {
              setIsOwner(true);
              setCurrentUserRole("owner");
            }
            // Will check for owner role in members after members are fetched if not primary owner
          } else {
            setIsOwner(false);
          }
          return workspaceData;
        }
        return null;
      } catch (error) {
        console.error("Error fetching workspace:", error);
        return null;
      }
    };

    // Fetch members
    const fetchMembers = async (workspaceData: Workspace | null): Promise<void> => {
      try {
        if (!workspaceData) return;

        const membersRef = collection(db, "workspaces", workspaceId, "members");
        const membersSnapshot = await getDocs(membersRef);

        const membersList: WorkspaceMemberWithDetails[] = await Promise.all(
          membersSnapshot.docs.map(async (memberDoc) => {
            const memberData = memberDoc.data();
            const userId = memberDoc.id;

            // Get email and displayName from member document as fallback
            const memberEmail = (memberData.email as string | undefined) || undefined;
            const memberDisplayName = (memberData.displayName as string | undefined) || undefined;

            // Fetch user details (may fail due to permissions)
            let name: string | undefined;
            let email: string | undefined;
            let photoURL: string | undefined;

            try {
              const userDoc = await getDoc(doc(db, "users", userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                name = (userData.name as string | undefined) || (userData.displayName as string | undefined);
                email = userData.email as string | undefined;
                photoURL = userData.photoURL as string | undefined;
              }
            } catch (error) {
              // User documents may not be accessible - use member document data as fallback
            }

            // Ensure owner role is set correctly
            const role = workspaceData.ownerId === userId ? "owner" : (memberData.role as "owner" | "member" | "admin");

            return {
              id: userId,
              role,
              joinedAt: memberData.joinedAt,
              // Use user document data if available, otherwise use member document data
              name: name || memberDisplayName,
              email: email || memberEmail,
              photoURL,
              status: "active" as const,
            };
          })
        );

        // Ensure workspace owner is always in the members list
        const ownerInList = membersList.find((m) => m.id === workspaceData.ownerId);
        
        // Get owner's member document data as fallback
        let ownerMemberEmail: string | undefined;
        let ownerMemberDisplayName: string | undefined;
        try {
          const ownerMemberDoc = await getDoc(doc(db, "workspaces", workspaceId, "members", workspaceData.ownerId));
          if (ownerMemberDoc.exists()) {
            const ownerMemberData = ownerMemberDoc.data();
            ownerMemberEmail = ownerMemberData.email as string | undefined;
            ownerMemberDisplayName = ownerMemberData.displayName as string | undefined;
          }
        } catch (error) {
          // Member document might not exist or not accessible - that's OK
        }
        
        if (!ownerInList) {
          // Fetch owner's user details (always try to fetch, even in demo mode)
          // In non-demo mode, this will work. In demo mode, it may fail due to permissions, which is OK
          let ownerName: string | undefined;
          let ownerEmail: string | undefined;
          let ownerPhotoURL: string | undefined;

          try {
            const ownerUserDoc = await getDoc(doc(db, "users", workspaceData.ownerId));
            if (ownerUserDoc.exists()) {
              const ownerUserData = ownerUserDoc.data();
              ownerName = (ownerUserData.name as string | undefined) || (ownerUserData.displayName as string | undefined);
              ownerEmail = ownerUserData.email as string | undefined;
              ownerPhotoURL = ownerUserData.photoURL as string | undefined;
            }
          } catch (error) {
            // In demo mode, user documents may not be accessible - use member document data as fallback
            console.log("Could not fetch owner details (may be demo mode):", error);
          }

          // Add owner to the list at the beginning
          // Use member document data as fallback if user document fetch failed
          membersList.unshift({
            id: workspaceData.ownerId,
            role: "owner",
            joinedAt: workspaceData.createdAt,
            name: ownerName || ownerMemberDisplayName,
            email: ownerEmail || ownerMemberEmail,
            photoURL: ownerPhotoURL,
            status: "active",
          });
        } else {
          // Owner is already in list, but ensure it's at the top and has latest user details
          const ownerIndex = membersList.findIndex((m) => m.id === workspaceData.ownerId);
          if (ownerIndex >= 0) {
            // Try to fetch latest owner user details to ensure we have real info (not "?")
            let ownerName: string | undefined;
            let ownerEmail: string | undefined;
            let ownerPhotoURL: string | undefined;
            
            try {
              const ownerUserDoc = await getDoc(doc(db, "users", workspaceData.ownerId));
              if (ownerUserDoc.exists()) {
                const ownerUserData = ownerUserDoc.data();
                ownerName = (ownerUserData.name as string | undefined) || (ownerUserData.displayName as string | undefined);
                ownerEmail = ownerUserData.email as string | undefined;
                ownerPhotoURL = ownerUserData.photoURL as string | undefined;
              }
            } catch (error) {
              // In demo mode, this may fail - use member document data as fallback
              console.log("Could not refresh owner details (may be demo mode):", error);
            }
            
            // Update owner details: priority is user document > existing > member document
            membersList[ownerIndex].name = ownerName || membersList[ownerIndex].name || ownerMemberDisplayName;
            membersList[ownerIndex].email = ownerEmail || membersList[ownerIndex].email || ownerMemberEmail;
            membersList[ownerIndex].photoURL = ownerPhotoURL || membersList[ownerIndex].photoURL;
            
            // Ensure owner role is set correctly
            membersList[ownerIndex].role = "owner";
            
            // Move to front if not already there
            if (ownerIndex > 0) {
              const ownerMember = membersList.splice(ownerIndex, 1)[0];
              membersList.unshift(ownerMember);
            }
          }
        }

        // Sort members: owner first, then others by name/email
        membersList.sort((a, b) => {
          // Owner always first
          if (a.id === workspaceData.ownerId) return -1;
          if (b.id === workspaceData.ownerId) return 1;
          // Then sort by name or email
          const aName = a.name || a.email || "";
          const bName = b.name || b.email || "";
          return aName.localeCompare(bName);
        });

        setMembers(membersList);

        let newIsOwner = false;
        let newCurrentUserRole: WorkspaceRole = "user";

        // Update current user's role and owner status
        if (user) {
          // Check if user is primary owner FIRST - primary owner always has owner role
          const isPrimaryOwner = user.uid === workspaceData.ownerId;

          if (isPrimaryOwner) {
            newCurrentUserRole = "owner";
            newIsOwner = true;
            setCurrentUserRole("owner");
            setIsOwner(true);
          } else {
            // User is not primary owner - check member document for role
            const currentMember = membersList.find((m) => m.id === user.uid);
            if (currentMember) {
              const memberRole = currentMember.role === "member" ? "user" : currentMember.role;
              newCurrentUserRole = memberRole as WorkspaceRole;
              newIsOwner = newCurrentUserRole === "owner";
              setCurrentUserRole(newCurrentUserRole);
              setIsOwner(newIsOwner);
            } else {
              setCurrentUserRole("user");
              setIsOwner(false);
            }
          }
        }

        updatePermissionsCache(workspaceId, {
          members: membersList,
          isOwner: newIsOwner,
          currentUserRole: newCurrentUserRole,
        });
      } catch (error) {
        console.error("Error fetching members:", error);
      }
    };

    // Initial data fetch
    const initializeData = async (): Promise<void> => {
      const workspaceData = await fetchWorkspace();
      if (workspaceData) {
        await fetchMembers(workspaceData);
      }
      setLoading(false);
    };

    initializeData();

    // Subscribe to workspace changes
    const unsubscribeWorkspace = onSnapshot(
      doc(db, "workspaces", workspaceId),
      async (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const workspaceData: Workspace = {
            id: snapshot.id,
            name: data.name as string,
            ownerId: data.ownerId as string,
            logoUrl: data.logoUrl as string | undefined,
            createdAt: data.createdAt,
          };
          setWorkspace(workspaceData);
          updatePermissionsCache(workspaceId, { workspace: workspaceData });
          // Owner status will be updated after members are fetched
          // But set it immediately if user is primary owner
          if (user) {
            const isPrimaryOwner = user.uid === workspaceData.ownerId;
            if (isPrimaryOwner) {
              setIsOwner(true);
              setCurrentUserRole("owner");
            }
          } else {
            setIsOwner(false);
          }
          await fetchMembers(workspaceData);
        }
      }
    );

    // Subscribe to members changes
    const unsubscribeMembers = onSnapshot(
      collection(db, "workspaces", workspaceId, "members"),
      async () => {
        const currentWorkspaceDoc = await getDoc(doc(db, "workspaces", workspaceId));
        if (currentWorkspaceDoc.exists()) {
          const data = currentWorkspaceDoc.data();
          const currentWorkspace: Workspace = {
            id: currentWorkspaceDoc.id,
            name: data.name as string,
            ownerId: data.ownerId as string,
            logoUrl: data.logoUrl as string | undefined,
            createdAt: data.createdAt,
          };
          await fetchMembers(currentWorkspace);
        }
      }
    );

    // Load permissions and custom roles from settings
    const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
    const unsubscribeSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as WorkspaceSettingsType;
        if (data.permissions) {
          const perms = {
            manageWorkspaceConfig: data.permissions.manageWorkspaceConfig || ["owner", "admin"],
            manageMembers: data.permissions.manageMembers || ["owner", "admin"],
            archiveAccounts: data.permissions.archiveAccounts || ["owner"],
            deleteTransactions: data.permissions.deleteTransactions || ["owner"],
            editTransactions: data.permissions.editTransactions || ["owner", "admin"],
            viewTransactionHistory: data.permissions.viewTransactionHistory || ["owner", "admin"],
            viewPermissions: data.permissions.viewPermissions || ["owner", "admin"],
            editPermissions: data.permissions.editPermissions || ["owner"],
            editPreferences: data.permissions.editPreferences || ["owner", "admin"],
            addCurrency: data.permissions.addCurrency || ["owner", "admin"],
            changeDefaultCurrency: data.permissions.changeDefaultCurrency || ["owner", "admin"],
            changeCurrencyRateDecimalPlaces: data.permissions.changeCurrencyRateDecimalPlaces || ["owner", "admin"],
          };
          setPermissions(perms);
          const roles = data.customRoles ?? [];
          const roleNames = data.customRoleNames ?? {};
          setCustomRoles(roles);
          setCustomRoleNames(roleNames);
          updatePermissionsCache(workspaceId, { permissions: perms, customRoles: roles, customRoleNames: roleNames });
        } else {
          setPermissions(DEFAULT_PERMISSIONS);
          setCustomRoles([]);
          setCustomRoleNames({});
          updatePermissionsCache(workspaceId, { permissions: DEFAULT_PERMISSIONS, customRoles: [], customRoleNames: {} });
        }
        } else {
          setPermissions(DEFAULT_PERMISSIONS);
          setCustomRoles([]);
          setCustomRoleNames({});
          updatePermissionsCache(workspaceId, { permissions: DEFAULT_PERMISSIONS, customRoles: [], customRoleNames: {} });
        }
    });

    return () => {
      unsubscribeWorkspace();
      unsubscribeMembers();
      unsubscribeSettings();
    };
  }, [workspaceId]);

  // Handle permission change
  const handlePermissionChange = async (
    permissionKey: keyof WorkspacePermissions,
    selectedRoles: WorkspaceRole[],
    changedRole?: WorkspaceRole
  ): Promise<void> => {
    // In demo mode, permissions cannot be changed
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Permissions cannot be changed. Please log in to modify workspace permissions." />
      );
      setSnackbarOpen(true);
      return;
    }

    // Check if user can edit permissions
    const canEdit = canEditPermissions();
    if (!workspaceId || !auth.currentUser || !canEdit) return;

    // Update local state optimistically for immediate UI feedback
    const updatedPermissions: WorkspacePermissions = {
      ...permissions,
      [permissionKey]: selectedRoles,
    };

    // Ensure owner is always included in required permissions
    if (permissionKey === "manageWorkspaceConfig" && !selectedRoles.includes("owner")) {
      updatedPermissions.manageWorkspaceConfig = ["owner", ...selectedRoles];
    }
    if (permissionKey === "manageMembers" && !selectedRoles.includes("owner")) {
      updatedPermissions.manageMembers = ["owner", ...selectedRoles];
    }
    if (permissionKey === "viewTransactionHistory" && !selectedRoles.includes("owner")) {
      updatedPermissions.viewTransactionHistory = ["owner", ...selectedRoles];
    }
    if (permissionKey === "viewPermissions" && !selectedRoles.includes("owner")) {
      updatedPermissions.viewPermissions = ["owner", ...selectedRoles];
    }
    if (permissionKey === "editPermissions" && !selectedRoles.includes("owner")) {
      updatedPermissions.editPermissions = ["owner", ...selectedRoles];
    }
    if (permissionKey === "editPreferences" && !selectedRoles.includes("owner")) {
      updatedPermissions.editPreferences = ["owner", ...selectedRoles];
    }

    // Update state immediately for instant UI feedback
    setPermissions(updatedPermissions);
    setSavingPermissionKey(permissionKey);
    if (changedRole) {
      setSavingRole(changedRole);
    }

    try {
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      await updateDoc(settingsRef, { permissions: updatedPermissions });
    } catch (error) {
      console.error("Error updating permissions:", error);
      alert("Failed to update permissions");
      // Revert to previous state on error
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        const data = settingsDoc.data() as WorkspaceSettingsType;
        if (data.permissions) {
          setPermissions({
            manageWorkspaceConfig: data.permissions.manageWorkspaceConfig || ["owner", "admin"],
            manageMembers: data.permissions.manageMembers || ["owner", "admin"],
            archiveAccounts: data.permissions.archiveAccounts || ["owner"],
            deleteTransactions: data.permissions.deleteTransactions || ["owner"],
            editTransactions: data.permissions.editTransactions ?? ["owner", "admin"],
            viewTransactionHistory: data.permissions.viewTransactionHistory || ["owner", "admin"],
            viewPermissions: data.permissions.viewPermissions || ["owner", "admin"],
            editPermissions: data.permissions.editPermissions || ["owner"],
            editPreferences: data.permissions.editPreferences ?? ["owner", "admin"],
            addCurrency: data.permissions.addCurrency || ["owner", "admin"],
            changeDefaultCurrency: data.permissions.changeDefaultCurrency || ["owner", "admin"],
            changeCurrencyRateDecimalPlaces: data.permissions.changeCurrencyRateDecimalPlaces || ["owner", "admin"],
          });
        }
      }
    } finally {
      setSavingPermissionKey(null);
      setSavingRole(null);
    }
  };

  // Toggle role in permission array
  const toggleRoleInPermission = (
    permissionKey: keyof WorkspacePermissions,
    role: WorkspaceRole
  ): void => {
    // In demo mode, show message and don't allow changes
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Permissions cannot be changed. Please log in to modify workspace permissions." />
      );
      setSnackbarOpen(true);
      return;
    }

    // Check if user can edit permissions
    const canEdit = canEditPermissions();
    // Only prevent changes to the specific checkbox being saved
    if (!canEdit || (savingPermissionKey === permissionKey && savingRole === role)) return;

    const currentRoles = permissions[permissionKey] || [];
    let newRoles: WorkspaceRole[];

    if (permissionKey === "manageWorkspaceConfig" && role === "owner") {
      // Owner cannot be removed from manageWorkspaceConfig
      return;
    }
    if (permissionKey === "manageMembers" && role === "owner") {
      // Owner cannot be removed from manageMembers
      return;
    }
    if (permissionKey === "viewTransactionHistory" && role === "owner") {
      // Owner cannot be removed from viewTransactionHistory
      return;
    }
    if (permissionKey === "viewPermissions" && role === "owner") {
      // Owner cannot be removed from viewPermissions
      return;
    }
    if (permissionKey === "editPermissions" && role === "owner") {
      // Owner cannot be removed from editPermissions
      return;
    }
    if (permissionKey === "editPreferences" && role === "owner") {
      // Owner cannot be removed from editPreferences
      return;
    }

    if (currentRoles.includes(role)) {
      newRoles = currentRoles.filter((r) => r !== role);
      // Allow empty array for deleteTransactions - owner can be removed
    } else {
      newRoles = [...currentRoles, role];
    }

    handlePermissionChange(permissionKey, newRoles, role);
  };

  // Get role display name
  const getRoleDisplayName = (role: WorkspaceRole): string => {
    // Check if it's a custom role
    const customRole = customRoles.find((r) => r.id === role);
    if (customRole) {
      return customRole.name;
    }

    // Check if there's a custom name for this standard role
    if (customRoleNames[role]) {
      return customRoleNames[role];
    }

    // Standard roles with default names
    const names: Record<string, string> = {
      owner: "Owner",
      admin: "Admin",
      user: "User",
      viewer: "Viewer",
    };
    return names[role] || role;
  };

  // Get members with specific role
  const getMembersWithRole = (role: WorkspaceRole): WorkspaceMemberWithDetails[] => {
    return members.filter((m) => {
      // Map legacy "member" role to "user" for comparison
      const memberRole = m.role === "member" ? "user" : m.role;
      return memberRole === role;
    });
  };

  // Permission definitions with categories and tooltips
  interface PermissionDefinition {
    key: keyof WorkspacePermissions;
    label: string;
    tooltip: string;
    category: string;
    ownerAlwaysHas?: boolean;
  }

  const permissionDefinitions: PermissionDefinition[] = [
    {
      key: "editPermissions",
      label: "Who can change permission",
      tooltip: "Control who can edit permissions and modify role access settings.",
      category: "GENERAL PERMISSIONS",
      ownerAlwaysHas: true,
    },
    {
      key: "viewTransactionHistory",
      label: "View Transaction History",
      tooltip: "Control who can view the transaction history and change logs.",
      category: "GENERAL PERMISSIONS",
      ownerAlwaysHas: true,
    },
    {
      key: "manageWorkspaceConfig",
      label: "Manage Workspace Configuration",
      tooltip: "Control who can modify workspace settings, logo, and general configuration.",
      category: "GENERAL PERMISSIONS",
      ownerAlwaysHas: true,
    },
    {
      key: "manageMembers",
      label: "Add/remove workspace members",
      tooltip: "Control who can invite and remove members. Owner can always remove any member; others cannot remove the owner.",
      category: "GENERAL PERMISSIONS",
      ownerAlwaysHas: true,
    },
    {
      key: "archiveAccounts",
      label: "Archive Accounts",
      tooltip: "Control who can archive accounts. Select one or more roles, or none to disable this feature.",
      category: "ACTION ACCESS",
    },
    {
      key: "deleteTransactions",
      label: "Delete Transactions",
      tooltip: "Control who can delete transactions. Select one or more roles, or none to disable this feature.",
      category: "ACTION ACCESS",
    },
    {
      key: "editTransactions",
      label: "Edit Transactions",
      tooltip: "Control who can edit transactions (fields, fee on transaction). Users without this permission see the Activity page but cannot edit or duplicate; edit dialog is read-only.",
      category: "ACTION ACCESS",
    },
    {
      key: "editPreferences",
      label: "Edit Preferences",
      tooltip: "Control who can edit workspace preferences (time, date, theme, fee toggles, transaction fields). Users without this permission see Settings but all preference controls are disabled.",
      category: "GENERAL PERMISSIONS",
      ownerAlwaysHas: true,
    },
    {
      key: "addCurrency",
      label: "Add Currency",
      tooltip: "Control who can add new currencies to the workspace.",
      category: "CURRENCY",
      ownerAlwaysHas: true,
    },
    {
      key: "changeDefaultCurrency",
      label: "Change Default Currency",
      tooltip: "Control who can change the workspace default currency.",
      category: "CURRENCY",
      ownerAlwaysHas: true,
    },
    {
      key: "changeCurrencyRateDecimalPlaces",
      label: "Change Rate & Decimal Places",
      tooltip: "Control who can edit exchange rates and decimal places for currencies.",
      category: "CURRENCY",
      ownerAlwaysHas: true,
    },
  ];

  // Get all unique categories
  const categories = Array.from(new Set(permissionDefinitions.map((p) => p.category)));

  // Available roles (excluding owner which is always first)
  // Include custom roles by their ID
  const availableRoles: WorkspaceRole[] = [
    "admin",
    "user",
    "viewer",
    ...customRoles.map((role) => role.id),
  ];

  // Handle role edit - allow editing all roles except owner
  const handleRoleEdit = (role: WorkspaceRole): void => {
    // Don't allow editing owner
    if (role === "owner") {
      return;
    }

    // Check if it's a custom role
    const customRole = customRoles.find((r) => r.id === role);
    if (customRole) {
      setEditingRoleId(customRole.id);
      setEditingRoleName(customRole.name);
      setEditRoleDialogOpen(true);
      return;
    }

    // It's a standard role (admin, user, viewer) - use custom name if exists, otherwise default
    const defaultNames: Record<string, string> = {
      admin: "Admin",
      user: "User",
      viewer: "Viewer",
    };
    setEditingRoleId(role);
    setEditingRoleName(customRoleNames[role] || defaultNames[role] || role);
    setEditRoleDialogOpen(true);
  };

  // Handle edit role submission
  const handleEditRoleSubmit = async (newRoleName: string): Promise<void> => {
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Roles cannot be edited. Please log in to edit roles." />
      );
      setSnackbarOpen(true);
      setEditRoleDialogOpen(false);
      return;
    }

    const canEdit = canEditPermissions();
    if (!workspaceId || !auth.currentUser || !canEdit || !editingRoleId) {
      setEditRoleDialogOpen(false);
      return;
    }

    setEditingRole(true);

    try {
      // Get current settings
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      const settingsDoc = await getDoc(settingsRef);
      const currentSettings = settingsDoc.exists()
        ? (settingsDoc.data() as WorkspaceSettingsType)
        : { defaultCurrency: "USD" };

      // Check if it's a custom role or standard role
      const isCustomRole = customRoles.some((r) => r.id === editingRoleId);
      
      if (isCustomRole) {
        // Update custom role name
        const updatedCustomRoles = (currentSettings.customRoles || []).map((role) =>
          role.id === editingRoleId ? { ...role, name: newRoleName } : role
        );

        // Update settings with new role name
        await updateDoc(settingsRef, {
          customRoles: updatedCustomRoles,
        });

        // Update local state
        setCustomRoles(updatedCustomRoles);
      } else {
        // Update custom name for standard role
        const updatedCustomRoleNames = {
          ...(currentSettings.customRoleNames || {}),
          [editingRoleId]: newRoleName,
        };

        // Update settings with new role name
        await updateDoc(settingsRef, {
          customRoleNames: updatedCustomRoleNames,
        });

        // Update local state
        setCustomRoleNames(updatedCustomRoleNames);
      }

      setEditRoleDialogOpen(false);
      setEditingRoleId(null);
      setEditingRoleName("");
    } catch (error) {
      console.error("Error editing role:", error);
      setSnackbarElement(
        <Alert severity="error">
          Failed to edit role: {error instanceof Error ? error.message : String(error)}
        </Alert>
      );
      setSnackbarOpen(true);
    } finally {
      setEditingRole(false);
    }
  };

  // Check if a role has any members assigned
  const checkRoleHasMembers = (roleId: WorkspaceRole): boolean => {
    return members.some((member) => {
      const memberRole = member.role === "member" ? "user" : member.role;
      return memberRole === roleId;
    });
  };

  // Handle role delete
  const handleRoleDelete = (role: WorkspaceRole): void => {
    // Don't allow deleting owner
    if (role === "owner") {
      return;
    }

    // Check if role has members
    const hasMembers = checkRoleHasMembers(role);
    
    // Get role display name
    const roleDisplayName = getRoleDisplayName(role);

    setDeletingRoleId(role);
    setDeletingRoleName(roleDisplayName);
    setDeleteRoleDialogOpen(true);
  };

  // Handle delete role submission
  const handleDeleteRoleSubmit = async (): Promise<void> => {
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Roles cannot be deleted. Please log in to delete roles." />
      );
      setSnackbarOpen(true);
      setDeleteRoleDialogOpen(false);
      return;
    }

    const canEdit = canEditPermissions();
    if (!workspaceId || !auth.currentUser || !canEdit || !deletingRoleId) {
      setDeleteRoleDialogOpen(false);
      return;
    }

    // Check if role has members
    const hasMembers = checkRoleHasMembers(deletingRoleId);
    if (hasMembers) {
      setSnackbarElement(
        <Alert severity="error">
          Cannot delete role: There are users assigned to this role. Please reassign or remove those users first.
        </Alert>
      );
      setSnackbarOpen(true);
      setDeleteRoleDialogOpen(false);
      return;
    }

    setDeletingRole(true);

    try {
      // Get current settings
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      const settingsDoc = await getDoc(settingsRef);
      const currentSettings = settingsDoc.exists()
        ? (settingsDoc.data() as WorkspaceSettingsType)
        : { defaultCurrency: "USD" };

      // Check if it's a custom role or standard role
      const isCustomRole = customRoles.some((r) => r.id === deletingRoleId);
      
      let updatedCustomRoles = currentSettings.customRoles || [];
      let updatedCustomRoleNames = { ...(currentSettings.customRoleNames || {}) };
      let updatedPermissions = { ...permissions };

      if (isCustomRole) {
        // Remove custom role from list
        updatedCustomRoles = updatedCustomRoles.filter((r) => r.id !== deletingRoleId);
      } else {
        // Remove custom name for standard role (revert to default)
        delete updatedCustomRoleNames[deletingRoleId];
      }

      // Remove role from all permissions
      Object.keys(updatedPermissions).forEach((key) => {
        const permissionKey = key as keyof WorkspacePermissions;
        if (updatedPermissions[permissionKey]) {
          updatedPermissions[permissionKey] = updatedPermissions[permissionKey]!.filter(
            (r) => r !== deletingRoleId
          );
        }
      });

      // Ensure owner is always included in required permissions
      if (!updatedPermissions.manageWorkspaceConfig?.includes("owner")) {
        updatedPermissions.manageWorkspaceConfig = ["owner", ...(updatedPermissions.manageWorkspaceConfig || [])];
      }
      if (!updatedPermissions.manageMembers?.includes("owner")) {
        updatedPermissions.manageMembers = ["owner", ...(updatedPermissions.manageMembers || [])];
      }
      if (!updatedPermissions.viewTransactionHistory?.includes("owner")) {
        updatedPermissions.viewTransactionHistory = ["owner", ...(updatedPermissions.viewTransactionHistory || [])];
      }
      if (!updatedPermissions.viewPermissions?.includes("owner")) {
        updatedPermissions.viewPermissions = ["owner", ...(updatedPermissions.viewPermissions || [])];
      }
      if (!updatedPermissions.editPermissions?.includes("owner")) {
        updatedPermissions.editPermissions = ["owner", ...(updatedPermissions.editPermissions || [])];
      }
      if (!updatedPermissions.editPreferences?.includes("owner")) {
        updatedPermissions.editPreferences = ["owner", ...(updatedPermissions.editPreferences || [])];
      }

      // Update settings
      await updateDoc(settingsRef, {
        customRoles: updatedCustomRoles,
        customRoleNames: updatedCustomRoleNames,
        permissions: updatedPermissions,
      });

      // Update local state
      setCustomRoles(updatedCustomRoles);
      setCustomRoleNames(updatedCustomRoleNames);
      setPermissions(updatedPermissions);
      setDeleteRoleDialogOpen(false);
      setDeletingRoleId(null);
      setDeletingRoleName("");
    } catch (error) {
      console.error("Error deleting role:", error);
      setSnackbarElement(
        <Alert severity="error">
          Failed to delete role: {error instanceof Error ? error.message : String(error)}
        </Alert>
      );
      setSnackbarOpen(true);
    } finally {
      setDeletingRole(false);
    }
  };

  // Handle create role dialog open
  const handleCreateRole = (): void => {
    setCreateRoleDialogOpen(true);
  };

  // Handle create role submission
  const handleCreateRoleSubmit = async (roleName: string, accessType: "full" | "none"): Promise<void> => {
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Roles cannot be created. Please log in to create roles." />
      );
      setSnackbarOpen(true);
      setCreateRoleDialogOpen(false);
      return;
    }

    const canEdit = canEditPermissions();
    if (!workspaceId || !auth.currentUser || !canEdit) {
      setCreateRoleDialogOpen(false);
      return;
    }

    setCreatingRole(true);

    try {
      // Generate a unique ID for the role (using lowercase name with spaces replaced by hyphens)
      const roleId = `custom_${roleName.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "")}_${Date.now()}`;

      // Create new custom role
      const newRole: CustomRole = {
        id: roleId,
        name: roleName,
        createdAt: Timestamp.now(),
      };

      // Get current settings
      const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
      const settingsDoc = await getDoc(settingsRef);
      const currentSettings = settingsDoc.exists()
        ? (settingsDoc.data() as WorkspaceSettingsType)
        : { defaultCurrency: "USD" };

      // Add custom role to list
      const updatedCustomRoles = [...(currentSettings.customRoles || []), newRole];

      // If full access, add role to all permissions
      let updatedPermissions = { ...permissions };
      if (accessType === "full") {
        const manageConfigRoles = permissions.manageWorkspaceConfig || [];
        const manageMembersRoles = permissions.manageMembers || ["owner", "admin"];
        const viewHistoryRoles = permissions.viewTransactionHistory || [];
        const editPrefRoles = permissions.editPreferences || ["owner", "admin"];
        
        // Ensure owner is always included where required
        updatedPermissions = {
          ...permissions,
          manageWorkspaceConfig: manageConfigRoles.includes("owner")
            ? [...manageConfigRoles, roleId]
            : ["owner", ...manageConfigRoles, roleId],
          manageMembers: manageMembersRoles.includes("owner")
            ? [...manageMembersRoles, roleId]
            : ["owner", ...manageMembersRoles, roleId],
          archiveAccounts: [...(permissions.archiveAccounts || []), roleId],
          deleteTransactions: [...(permissions.deleteTransactions || []), roleId],
          editTransactions: [...(permissions.editTransactions || ["owner", "admin"]), roleId],
          viewTransactionHistory: viewHistoryRoles.includes("owner")
            ? [...viewHistoryRoles, roleId]
            : ["owner", ...viewHistoryRoles, roleId],
          editPreferences: editPrefRoles.includes("owner")
            ? [...editPrefRoles, roleId]
            : ["owner", ...editPrefRoles, roleId],
        };
      }

      // Update settings with new role and permissions
      await updateDoc(settingsRef, {
        customRoles: updatedCustomRoles,
        permissions: updatedPermissions,
      });

      // Update local state
      setCustomRoles(updatedCustomRoles);
      setPermissions(updatedPermissions);
      setCreateRoleDialogOpen(false);
    } catch (error) {
      console.error("Error creating role:", error);
      setSnackbarElement(
        <Alert severity="error">
          Failed to create role: {error instanceof Error ? error.message : String(error)}
        </Alert>
      );
      setSnackbarOpen(true);
    } finally {
      setCreatingRole(false);
    }
  };

  // Check if permission is checked for a role
  const isPermissionChecked = (permissionKey: keyof WorkspacePermissions, role: WorkspaceRole): boolean => {
    // Owner always has certain permissions - check this first
    if (role === "owner") {
      const def = permissionDefinitions.find((p) => p.key === permissionKey);
      if (def?.ownerAlwaysHas) return true;
    }
    
    const permission = permissions[permissionKey];
    if (!permission) return false;
    
    return permission.includes(role);
  };

  // Check if permission checkbox is disabled
  const isPermissionDisabled = (permissionKey: keyof WorkspacePermissions, role: WorkspaceRole): boolean => {
    if (role === "owner") {
      const def = permissionDefinitions.find((p) => p.key === permissionKey);
      if (def?.ownerAlwaysHas) return true;
    }
    return false;
  };

  if (loading) {
    return <LoadingSpinner fullPage={true} />;
  }

  if (!workspace) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Workspace not found</Alert>
      </Box>
    );
  }

  // Only users with editPermissions can change permissions; everyone can view the page
  const canEditPermissions = (): boolean => {
    if (isOwner || currentUserRole === "owner") return true;
    if (isDemoMode()) return false; // Demo mode is read-only
    const editPerms = permissions.editPermissions || ["owner"];
    return editPerms.includes(currentUserRole);
  };

  const canManagePermissions = canEditPermissions();

  const padding = isMobile ? 1.5 : 3;
  const cardMargin = isMobile ? 2 : 3;
  const contentPaddingSx = fullWidth ? { py: padding, px: isMobile ? 1.5 : 2, pb: 2 } : { p: padding, pb: 2 };
  const headerPaddingSx = fullWidth ? { py: isMobile ? 1.5 : 2, px: isMobile ? 1.5 : 2 } : { p: isMobile ? 1.5 : 2 };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%", overflow: "hidden" }}>
      {!canManagePermissions && (
        <Alert severity="info" sx={{ mx: fullWidth ? (isMobile ? 1.5 : 2) : padding, mt: 1, mb: 0 }}>
          You can view permissions. Only users with &quot;Who can change permission&quot; can edit.
        </Alert>
      )}
      {/* Mobile: sticky header + content */}
      {isMobile && (
        <Paper
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            ...headerPaddingSx,
            position: "sticky",
            top: 0,
            zIndex: 10,
            bgcolor: "background.paper",
            mb: 2,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "stretch",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateRole}
            disabled={!canManagePermissions}
            fullWidth
          >
            Create Role
          </Button>
        </Paper>
      )}

      {/* Mobile: Roles list (edit/delete) + Card-based permissions */}
      {isMobile ? (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...contentPaddingSx }}>
          <Stack spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 1, px: 0.5 }}>
              Roles
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
              <Stack spacing={0.5}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 0.5 }}>
                  <Typography variant="body2" fontWeight="500">Owner</Typography>
                  <Typography variant="caption" color="text.secondary">Full access</Typography>
                </Box>
                {availableRoles.map((role) => {
                  const canEdit = role !== "owner";
                  const hasMembers = checkRoleHasMembers(role);
                  return (
                    <Box
                      key={role}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 0.5,
                        borderTop: 1,
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2">{getRoleDisplayName(role)}</Typography>
                      {canEdit && (
                        <Box sx={{ display: "flex", gap: 0.25 }}>
                          <Tooltip title="Edit role name">
                            <IconButton size="small" onClick={() => handleRoleEdit(role)} disabled={!canManagePermissions}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={hasMembers ? "Users assigned – remove them first" : "Delete role"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleRoleDelete(role)}
                                disabled={!canManagePermissions || hasMembers}
                                sx={{ color: hasMembers ? "text.disabled" : "error.main" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          </Box>
          {categories.map((category) => {
            const categoryPermissions = permissionDefinitions.filter((p) => p.category === category);
            return (
              <Box key={category}>
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: 1,
                    px: 0.5,
                  }}
                >
                  {category}
                </Typography>
                <Stack spacing={1.5}>
                  {categoryPermissions.map((permission) => (
                    <Paper key={permission.key} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1 }}>
                        <Typography variant="body2" fontWeight="500">
                          {permission.label}
                        </Typography>
                        <InfoTooltipIcon
                          tooltipMessage={permission.tooltip}
                          position="inline"
                          size="small"
                        />
                      </Box>
                      <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isPermissionChecked(permission.key, "owner")}
                              disabled={isPermissionDisabled(permission.key, "owner") || !canManagePermissions}
                              onChange={() => toggleRoleInPermission(permission.key, "owner")}
                              size="small"
                            />
                          }
                          label="Owner"
                        />
                        {availableRoles.map((role) => (
                          <FormControlLabel
                            key={role}
                            control={
                              <Checkbox
                                checked={isPermissionChecked(permission.key, role)}
                                disabled={!canManagePermissions || (savingPermissionKey === permission.key && savingRole === role)}
                                onChange={() => toggleRoleInPermission(permission.key, role)}
                                size="small"
                              />
                            }
                            label={getRoleDisplayName(role)}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            );
          })}
          </Stack>
        </Box>
      ) : (
      /* Desktop: Card layout like Settings > Workspace (title + Divider + content) */
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "auto", ...(fullWidth ? { py: padding, px: 2, pb: 2, pt: 0 } : { p: padding, pb: 2, pt: 0 }) }}>
        <Card sx={{ mb: cardMargin, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <CardContent sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
              <Typography variant="h5">Permissions</Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateRole}
                disabled={!canManagePermissions}
              >
                Create Role
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <TableContainer sx={{ flex: 1, minHeight: 0, overflowX: "auto", overflowY: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      minWidth: 350,
                      maxWidth: 350,
                      width: 350,
                      px: 3,
                      py: 2,
                      position: "sticky",
                      left: 0,
                      zIndex: 10,
                      backgroundColor: "background.paper",
                      borderRight: 1,
                      borderColor: "divider",
                    }}
                  >
                    Permission
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "bold",
                      minWidth: 120,
                      px: 2,
                      py: 2,
                      backgroundColor: "background.paper",
                    }}
                  >
                    Owner
                  </TableCell>
                  {availableRoles.map((role) => {
                    // Show edit/delete icons for all roles except owner
                    const canEdit = role !== "owner";
                    const hasMembers = checkRoleHasMembers(role);
                    return (
                      <TableCell
                        key={role}
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          minWidth: 140,
                          px: 2,
                          py: 2,
                          backgroundColor: "background.paper",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, whiteSpace: "nowrap" }}>
                          <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100px" }}>
                            {getRoleDisplayName(role)}
                          </Typography>
                          {canEdit && (
                            <>
                              <Tooltip title="Edit role">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRoleEdit(role)}
                                    disabled={!canManagePermissions}
                                    sx={{ p: 0.25, flexShrink: 0 }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={hasMembers ? "Cannot delete: Users assigned to this role" : "Delete role"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRoleDelete(role)}
                                    disabled={!canManagePermissions || hasMembers}
                                    sx={{ p: 0.25, flexShrink: 0, color: hasMembers ? "text.disabled" : "error.main" }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category, categoryIndex) => {
                  const categoryPermissions = permissionDefinitions.filter((p) => p.category === category);
                  return (
                    <ReactFragment key={category}>
                      {/* Category Header Row */}
                      <TableRow>
                        <TableCell
                          colSpan={1 + availableRoles.length + 1}
                          sx={{
                            backgroundColor: "action.hover",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                            fontSize: "0.75rem",
                            py: 1.5,
                            px: 3,
                            borderBottom: categoryIndex < categories.length - 1 ? 1 : 0,
                            borderColor: "divider",
                            position: "sticky",
                            left: 0,
                            zIndex: 5,
                          }}
                        >
                          {category}
                        </TableCell>
                      </TableRow>
                      {/* Permission Rows */}
                      {categoryPermissions.map((permission, permIndex) => (
                        <TableRow
                          key={permission.key}
                          sx={{
                            borderBottom:
                              permIndex < categoryPermissions.length - 1 ||
                              categoryIndex < categories.length - 1
                                ? 1
                                : 0,
                            borderColor: "divider",
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                          }}
                        >
                          {/* Permission Name Cell - Sticky */}
                          <TableCell
                            sx={{
                              py: 1.5,
                              px: 3,
                              position: "sticky",
                              left: 0,
                              zIndex: 3,
                              backgroundColor: "background.paper",
                              borderRight: 1,
                              borderColor: "divider",
                              minWidth: 350,
                              maxWidth: 350,
                              width: 350,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography variant="body2">{permission.label}</Typography>
                              <InfoTooltipIcon
                                tooltipMessage={permission.tooltip}
                                position="inline"
                                size="small"
                                sx={{ ml: 0.25 }}
                              />
                            </Box>
                          </TableCell>
                          {/* Owner Column */}
                          <TableCell align="center" sx={{ py: 1.5, px: 2 }}>
                              <Checkbox
                                checked={isPermissionChecked(permission.key, "owner")}
                                disabled={isPermissionDisabled(permission.key, "owner") || !canManagePermissions}
                                onChange={() => toggleRoleInPermission(permission.key, "owner")}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                          </TableCell>
                          {/* Other Role Columns */}
                          {availableRoles.map((role) => (
                            <TableCell key={role} align="center" sx={{ py: 1.5, px: 2 }}>
                              <Checkbox
                                checked={isPermissionChecked(permission.key, role)}
                                disabled={!canManagePermissions || (savingPermissionKey === permission.key && savingRole === role)}
                                onChange={() => toggleRoleInPermission(permission.key, role)}
                                size="small"
                                sx={{ p: 0.5 }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </ReactFragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      </Box>
      )}
      {isDemoMode() && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Demo mode: You can view permissions but cannot modify them. Please log in to change permissions.
        </Alert>
      )}


      {/* Create Role Dialog */}
      <CreateRoleDialog
        open={createRoleDialogOpen}
        onClose={() => setCreateRoleDialogOpen(false)}
        onCreate={handleCreateRoleSubmit}
        creating={creatingRole}
      />

      {/* Edit Role Dialog */}
      <EditRoleDialog
        open={editRoleDialogOpen}
        onClose={() => {
          setEditRoleDialogOpen(false);
          setEditingRoleId(null);
          setEditingRoleName("");
        }}
        onSave={handleEditRoleSubmit}
        saving={editingRole}
        currentRoleName={editingRoleName}
        roleId={editingRoleId || ""}
      />

      {/* Delete Role Dialog */}
      <DeleteRoleDialog
        open={deleteRoleDialogOpen}
        onClose={() => {
          setDeleteRoleDialogOpen(false);
          setDeletingRoleId(null);
          setDeletingRoleName("");
        }}
        onDelete={handleDeleteRoleSubmit}
        deleting={deletingRole}
        roleName={deletingRoleName}
        hasMembers={deletingRoleId ? checkRoleHasMembers(deletingRoleId) : false}
      />

      {/* Snackbar for demo mode alerts */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbarOpen(false);
          setSnackbarElement(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => {
          setSnackbarOpen(false);
          setSnackbarElement(null);
        }} severity="info" sx={{ width: "100%" }}>
          {snackbarElement || snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PermissionsSettings;
